import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { BaseEditCopyService } from 'tms-lib';
import {
  AddressLabel,
  Customer,
  CustomerCreditNotes,
  CustomerDetails,
  CustomerKeyAndName,
  CustomerKeyFormat,
  CustomerNextKey,
  CustomerOwnerDetails,
  CustomerSalesInvoiceHistory,
  CustomerSalesOrderHistory,
  SalesOrderCustomer,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class CustomerService extends BaseEditCopyService<Customer, CustomerDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('Customer', $localize`Customer`);
  }

  getCustomerForSalesOrder(id: string): Observable<SalesOrderCustomer> {
    return this.httpService.get<SalesOrderCustomer>(`${this.apiUrl}/SalesOrder/${id}`);
  }

  getOwnerDetails(id: string): Observable<CustomerOwnerDetails> {
    return this.httpService.get<CustomerOwnerDetails>(`${this.apiUrl}/GetOwnerDetails?id=${id}`);
  }

  getCustomerKeyAndName(id: string): Observable<CustomerKeyAndName> {
    return this.httpService.get<CustomerKeyAndName>(`${this.apiUrl}/GetKeyAndName/${id}`);
  }

  checkKey(key: string): Observable<boolean> {
    // Encoded, so "CC&B01" is checked as CC&B01 rather than as "CC" (Ross Fabrics, Sep 2026).
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckKey?key=${encodeURIComponent(key ?? '')}`);
  }

  /** The selling company's customer key format (e.g. AAAANN), or null - drives the Next number button. */
  getKeyFormat(sellingCompanyId: string): Observable<CustomerKeyFormat> {
    return this.httpService.get<CustomerKeyFormat>(`${this.apiUrl}/KeyFormat?sellingCompanyId=${sellingCompanyId}`);
  }

  /**
   * The next free key for a prefix under the selling company's key format (ROSS -> ROSS08). The
   * prefix goes as typed - not trimmed - and is refused with a readable message when it is too short
   * or too long, or when the numbers have run out.
   */
  nextKey(prefix: string, sellingCompanyId: string): Observable<CustomerNextKey> {
    return this.httpService.get<CustomerNextKey>(
      `${this.apiUrl}/NextKey?prefix=${encodeURIComponent(prefix ?? '')}&sellingCompanyId=${sellingCompanyId}`,
    );
  }

  getCustomerSalesOrderHistory(
    id: string,
    liveOrders: boolean,
    search: string,
    showTotalValue: boolean,
  ): Observable<CustomerSalesOrderHistory[]> {
    return this.httpService.get<CustomerSalesOrderHistory[]>(
      `${this.apiUrl}/GetCustomerSalesOrderHistory?id=${id}&liveOrders=${liveOrders}&search=${search}&showTotalValue=${showTotalValue}`,
    );
  }

  getCustomerSalesInvoiceHistory(id: string, search: string): Observable<CustomerSalesInvoiceHistory[]> {
    return this.httpService.get<CustomerSalesInvoiceHistory[]>(
      `${this.apiUrl}/CustomerSalesInvoiceHistory?id=${id}&search=${search}`,
    );
  }

  getCustomerCreditNotes(id: string, search: string): Observable<CustomerCreditNotes[]> {
    return this.httpService.get<CustomerCreditNotes[]>(`${this.apiUrl}/CustomerCreditNotes?id=${id}&search=${search}`);
  }

  getDeliveryAddress(id: string): Observable<AddressLabel> {
    return this.httpService.get<AddressLabel>(`${this.apiUrl}/GetDeliveryAddress?id=${id}`);
  }

  exportFtpData(): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/FtpCustomerData`, {});
  }
}
